import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { toast } from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // 处理401错误
    if (error.response?.status === 401) {
      // 清除本地存储的token
      localStorage.removeItem('token')
      
      // 显示错误提示
      toast.error('登录已过期，请重新登录')
      
      // 跳转到登录页面
      window.location.href = '/login'
      
      return Promise.reject(error)
    }

    // 处理网络错误
    if (!error.response) {
      toast.error('网络连接失败，请检查网络设置')
      return Promise.reject(error)
    }

    // 处理其他HTTP错误
    const errorMessage = error.response.data?.message || '请求失败'
    toast.error(errorMessage)

    return Promise.reject(error)
  }
)

// 导出axios实例
export default apiClient

// 导出便捷的API方法
export const api = {
  get: <T = any>(url: string, config?: any) => 
    apiClient.get<T>(url, config).then(response => response.data),
  
  post: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.post<T>(url, data, config).then(response => response.data),
  
  put: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.put<T>(url, data, config).then(response => response.data),
  
  delete: <T = any>(url: string, config?: any) => 
    apiClient.delete<T>(url, config).then(response => response.data),
  
  patch: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.patch<T>(url, data, config).then(response => response.data),
}

// 统一的fetch包装函数，行为类似原生fetch但会自动处理401错误
export const fetchWithAuth = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  const token = localStorage.getItem('token')
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    ...(token && { Authorization: `Bearer ${token}` }),
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    })

    // 检查401错误
    if (response.status === 401) {
      // 清除本地存储的token
      localStorage.removeItem('token')
      
      // 显示错误提示
      toast.error('登录已过期，请重新登录')
      
      // 跳转到首页
      window.location.href = '/login'
      
      // 抛出错误以阻止后续处理
      throw new Error('Unauthorized')
    }

    return response
  } catch (error) {
    // 网络错误等其他错误直接抛出
    throw error
  }
}

// 带重试机制的API调用
export const apiWithRetry = async <T>(
  apiCall: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await apiCall()
    } catch (error: any) {
      lastError = error
      
      // 如果是401错误，不重试
      if (error.response?.status === 401) {
        throw error
      }
      
      // 如果是网络错误，等待后重试
      if (!error.response && i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)))
        continue
      }
      
      throw error
    }
  }

  throw lastError
}

// 带加载状态的API调用
export const apiWithLoading = async <T>(
  apiCall: () => Promise<T>,
  setLoading: (loading: boolean) => void
): Promise<T> => {
  try {
    setLoading(true)
    return await apiCall()
  } finally {
    setLoading(false)
  }
} 